import React, { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { IMAGES } from "../data/content";

const CLOTHING = ["Dress / Gown", "Suit / Blazer", "Lehenga", "Sherwani", "Saree / Blouse", "Kurta Set", "Other"];
const OCCASIONS = ["Wedding", "Festive", "Everyday", "Formal / Business", "Party / Cocktail", "Other"];
const BUDGETS = ["Under ₹15,000", "₹15,000 – ₹40,000", "₹40,000 – ₹1,00,000", "₹1,00,000+"];

const empty = {
  name: "", email: "", phone: "", clothing_type: "", style: "", colors: "",
  measurements: "", occasion: "", budget: "", requirements: "", reference_link: "",
};

export default function CustomOrders() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.clothing_type) {
      toast.error("Please fill your name, email and clothing type.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/custom-orders", form);
      setDone(true);
      toast.success("Your custom order request has been received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        overline="Custom Orders"
        title={["Made only", "for you."]}
        subtitle="Tell us what you envision. From the first sketch to the final stitch, we craft your garment to your exact story and measurements."
      />

      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="img-frame rounded-sm">
                <img src={IMAGES.atelier2} alt="Atelier" className="w-full h-[46vh] object-cover rounded-sm" />
              </div>
              <div className="mt-8 space-y-6">
                {[
                  ["01", "Share your vision", "Fill the brief with your idea, occasion and references."],
                  ["02", "We respond in 48 hours", "Our atelier reviews and reaches out with a proposal."],
                  ["03", "Crafted to measure", "Your piece is patterned, cut and hand-finished for you."],
                ].map(([n, t, d]) => (
                  <div key={n} className="flex gap-4">
                    <span className="font-serif text-2xl text-[#C6A15B]/70">{n}</span>
                    <div>
                      <p className="font-medium">{t}</p>
                      <p className="text-[#7a7568] text-sm mt-1">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - form */}
          <div className="lg:col-span-8">
            {done ? (
              <FadeUp>
                <div data-testid="order-success" className="border border-[#C6A15B] bg-white/60 rounded-sm p-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#C6A15B] text-[#121212] flex items-center justify-center mx-auto">
                    <Check strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-3xl font-light mt-6">Thank you, {form.name.split(" ")[0]}.</h3>
                  <p className="text-[#7a7568] mt-3 max-w-md mx-auto">
                    Your custom order request is with our atelier. We'll be in touch within 48 hours.
                  </p>
                  <button
                    data-testid="new-order-btn"
                    onClick={() => { setForm(empty); setDone(false); }}
                    className="btn-outline mt-8 px-8 py-3 overline"
                  >
                    <span>Submit another request</span>
                  </button>
                </div>
              </FadeUp>
            ) : (
              <form data-testid="custom-order-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                <Field label="Full name *"><input data-testid="input-name" className="field" value={form.name} onChange={set("name")} placeholder="Your name" /></Field>
                <Field label="Email *"><input data-testid="input-email" type="email" className="field" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
                <Field label="Phone"><input data-testid="input-phone" className="field" value={form.phone} onChange={set("phone")} placeholder="+91" /></Field>
                <Field label="Clothing type *">
                  <select data-testid="input-clothing" className="field" value={form.clothing_type} onChange={set("clothing_type")}>
                    <option value="">Select</option>
                    {CLOTHING.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Occasion">
                  <select data-testid="input-occasion" className="field" value={form.occasion} onChange={set("occasion")}>
                    <option value="">Select</option>
                    {OCCASIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Budget">
                  <select data-testid="input-budget" className="field" value={form.budget} onChange={set("budget")}>
                    <option value="">Select</option>
                    {BUDGETS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Preferred style"><input data-testid="input-style" className="field" value={form.style} onChange={set("style")} placeholder="e.g. minimal, embroidered, draped" /></Field>
                <Field label="Colours"><input data-testid="input-colors" className="field" value={form.colors} onChange={set("colors")} placeholder="e.g. ivory, gold, deep red" /></Field>
                <Field label="Measurements" full><textarea data-testid="input-measurements" rows={2} className="field resize-none" value={form.measurements} onChange={set("measurements")} placeholder="Bust / waist / hips / length, or note that you'd like a fitting" /></Field>
                <Field label="Requirements & vision" full><textarea data-testid="input-requirements" rows={4} className="field resize-none" value={form.requirements} onChange={set("requirements")} placeholder="Describe the garment you're dreaming of..." /></Field>
                <Field label="Reference image link" full><input data-testid="input-reference" className="field" value={form.reference_link} onChange={set("reference_link")} placeholder="Paste a link to an inspiration image (optional)" /></Field>

                <div className="sm:col-span-2 pt-4">
                  <button data-testid="submit-order-btn" disabled={loading} className="btn-gold px-10 py-4 overline disabled:opacity-60">
                    <span className="inline-flex items-center gap-2">
                      {loading && <Loader2 size={16} className="animate-spin" />}
                      {loading ? "Sending..." : "Submit custom order"}
                    </span>
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

const Field = ({ label, children, full }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="overline text-[#7a7568] block mb-2">{label}</span>
    {children}
  </label>
);
